<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import type { EmployeeOption } from '../types/finance'

/**
 * Employee picker for payroll items.
 *
 * `/employees` is not searchable per term, so the production staff list is loaded
 * once and filtered client-side, with the shared picker handling paging.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    selectedId?: string | number | null
  }>(),
  {
    title: '',
    selectedId: null,
  },
)

const emit = defineEmits<{
  close: []
  select: [employee: EmployeeOption]
}>()

const { t } = useI18n()
const { listEmployeeOptions } = useFinanceApi()

const PAGE_SIZE = 20

const search = ref('')
const employees = ref<EmployeeOption[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)

function employeeName(employee: EmployeeOption): string {
  return employee.name || employee.full_name || ''
}

function employeeCode(employee: EmployeeOption): string {
  return employee.employee_code || employee.code || ''
}

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return employees.value
  return employees.value.filter((employee) =>
    [employeeName(employee), employeeCode(employee)]
      .some((value) => value.toLowerCase().includes(term)),
  )
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / PAGE_SIZE)))

const items = computed<SmartPickerOption[]>(() =>
  filtered.value
    .slice((page.value - 1) * PAGE_SIZE, page.value * PAGE_SIZE)
    .map((employee) => ({
      id: employee.id,
      title: employeeName(employee),
      subtitle: employeeCode(employee) || undefined,
      meta: employee.department_name || undefined,
    })),
)

const rangeLabel = computed(() => {
  const total = filtered.value.length
  if (!total) return ''
  const from = (page.value - 1) * PAGE_SIZE + 1
  return t('finance.picker.pageRange', {
    from,
    to: Math.min(page.value * PAGE_SIZE, total),
    total,
  })
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    employees.value = await listEmployeeOptions()
  } catch (cause) {
    error.value = formatApiError(cause)
    employees.value = []
  } finally {
    loading.value = false
  }
}

function handleSelect(item: SmartPickerOption) {
  const employee = employees.value.find((entry) => String(entry.id) === String(item.id))
  if (employee) emit('select', employee)
}

watch(search, () => {
  page.value = 1
})

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    search.value = ''
    page.value = 1
    void load()
  },
)

onMounted(() => {
  if (props.open) void load()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title || t('finance.payrollFlow.employeePickerTitle')"
    :eyebrow="t('finance.payrollFlow.employeePickerEyebrow')"
    :search-label="t('finance.payrollFlow.employeePickerSearch')"
    :search-placeholder="t('finance.payrollFlow.employeePickerPlaceholder')"
    :search-value="search"
    :loading="loading"
    :error-message="error"
    :empty-message="t('finance.payrollFlow.employeePickerEmpty')"
    :items="items"
    :page="page"
    :total-pages="totalPages"
    :selected-id="selectedId"
    :page-label="rangeLabel"
    :close-aria-label="t('finance.picker.close')"
    :retry-label="t('finance.picker.retry')"
    :previous-label="t('finance.picker.previous')"
    :next-label="t('finance.picker.next')"
    :cancel-label="t('finance.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page = Math.max(1, page - 1)"
    @next="page = Math.min(totalPages, page + 1)"
    @update:search-value="search = $event"
    @select="handleSelect"
  />
</template>
